import { z } from "zod";

/**
 * Wire contract para los comentarios in-line anclados en archivos LaTeX
 * (ADR 021).
 *
 * El anclaje usa la estrategia "texto + contexto" (B): persistimos
 * (lineaInicio, columnaInicio, lineaFin, columnaFin) como posición
 * optimista, MÁS el texto seleccionado completo + 50 chars de contexto
 * antes/después. El frontend reposiciona buscando el patrón con
 * contexto, sin contexto, o marca como obsoleto. Ver §2.2 del ADR.
 */

// ────────────────────────── Estado ──────────────────────────────

export const ESTADO_COMENTARIO = ["ABIERTO", "RESUELTO"] as const;
export type EstadoComentarioLatex = (typeof ESTADO_COMENTARIO)[number];

// ────────────────────────── Schemas Zod ──────────────────────────

const lineaSchema = z.number().int().nonnegative();
const columnaSchema = z.number().int().nonnegative();

/**
 * Path de archivo dentro del proyecto. Validado por regex en el backend
 * (`validarPathArchivo` — ADR 013 §5). Acá solo limitamos longitud
 * para no aceptar payloads abusivos.
 */
const archivoPathSchema = z
  .string()
  .min(1, "El path del archivo es obligatorio")
  .max(512, "El path no puede exceder 512 caracteres");

/**
 * Schema de creación de un comentario raíz o respuesta.
 *
 * - Si `parentId` viene presente, es respuesta — el backend ignora
 *   el anclaje y hereda el del comentario raíz. Aún así pedimos
 *   anclaje para mantener un solo schema (más simple); el backend lo
 *   sobreescribe.
 * - `textoAnclado` es el texto que el usuario seleccionó (1–2000
 *   chars). Sirve para reposicionar y para mostrarlo al lector como
 *   "quote" cuando se renderiza el comentario.
 * - `contextoAntes/Despues` son los chars inmediatos antes/después
 *   del texto anclado (≤50 cada uno). Permiten matching cuando hay
 *   ambigüedad (palabra común que aparece varias veces).
 */
export const crearComentarioLatexSchema = z.object({
  archivoPath: archivoPathSchema,
  lineaInicio: lineaSchema,
  columnaInicio: columnaSchema,
  lineaFin: lineaSchema,
  columnaFin: columnaSchema,
  textoAnclado: z
    .string()
    .min(1, "El texto anclado no puede estar vacío")
    .max(2000, "El texto anclado no puede exceder 2000 caracteres"),
  contextoAntes: z.string().max(200, "El contexto antes no puede exceder 200 caracteres"),
  contextoDespues: z.string().max(200, "El contexto después no puede exceder 200 caracteres"),
  contenido: z
    .string()
    .min(1, "El comentario no puede estar vacío")
    .max(2000, "El comentario no puede exceder 2000 caracteres"),
  parentId: z.string().uuid().optional(),
});
export type CrearComentarioLatexInput = z.infer<typeof crearComentarioLatexSchema>;

/**
 * Re-anclar un comentario manualmente cuando quedó obsoleto. El frontend
 * lo dispara cuando el usuario selecciona un nuevo rango sobre un
 * comentario marcado con badge "⚠️ Texto modificado".
 */
export const reanclarComentarioLatexSchema = z.object({
  lineaInicio: lineaSchema,
  columnaInicio: columnaSchema,
  lineaFin: lineaSchema,
  columnaFin: columnaSchema,
  textoAnclado: z.string().min(1).max(2000),
  contextoAntes: z.string().max(200),
  contextoDespues: z.string().max(200),
});
export type ReanclarComentarioLatexInput = z.infer<typeof reanclarComentarioLatexSchema>;

/**
 * Listado: filtros opcionales en query string. Sin filtros devuelve
 * todos los comentarios del proyecto agrupados por archivo.
 */
export const listarComentariosLatexQuerySchema = z.object({
  archivoPath: archivoPathSchema.optional(),
  estado: z.enum(ESTADO_COMENTARIO).optional(),
});
export type ListarComentariosLatexQueryInput = z.infer<typeof listarComentariosLatexQuerySchema>;

// ────────────────────────── Tipos del wire de salida ──────────────────────────

export interface AutorComentarioWire {
  id: string;
  nombres: string;
  apellidos: string;
  rol: string;
}

/**
 * Representación de un comentario tal como lo devuelve el backend.
 *
 * Las respuestas vienen anidadas en `respuestas` (un nivel — los
 * comentarios raíz tienen respuestas, las respuestas no anidan más).
 * Esta forma simplifica el render del frontend (mapear el thread es
 * un solo pase).
 */
export interface ComentarioArchivoLatexWire {
  id: string;
  proyectoId: string;
  archivoPath: string;
  autor: AutorComentarioWire;

  /** Anclaje persistido (la última posición conocida correcta). */
  lineaInicio: number;
  columnaInicio: number;
  lineaFin: number;
  columnaFin: number;
  textoAnclado: string;
  contextoAntes: string;
  contextoDespues: string;

  contenido: string;
  parentId: string | null;

  estado: EstadoComentarioLatex;
  resueltoPor: AutorComentarioWire | null;
  fechaResolucion: string | null;

  fechaCreacion: string;
  fechaActualizacion: string;

  /** Solo viene en comentarios raíz; vacío en respuestas. */
  respuestas: ComentarioArchivoLatexWire[];
}
